
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

export const Provider = {
    Xero: 'xero',
    QuickBooksOnline: 'qbo',
    Gusto: 'gusto'
};

export type ProviderType = typeof Provider[keyof typeof Provider];
export const ConnectionStatus = {
    Connected: 'connected',
    Disconnected: 'disconnected',
    Error: 'error'
};

export type ConnectionStatusType = typeof ConnectionStatus[keyof typeof ConnectionStatus];

// Provider-specific metadata (realmId for QBO, tenantId for Xero, companyUuid for Gusto)
export type AccountingConnectionProviderMeta = {realmId?: string, tenantId?: string, companyUuid?: string, useSandbox?: boolean};

 /**
 * AccountingConnection
 *
 * Stores the OAuth credentials and connection state for a firm's external financial software integration (Xero, QuickBooks Online, or Gusto). Each firm may have at most one active connection. Holds encrypted access and refresh tokens, token expiry, provider-specific metadata (tenantId, realmId, companyUuid) used to map Outlaw bank accounts to external accounts, and a connection status that tracks whether the integration is active or in an error state.
 */
class AccountingConnection extends Base implements EntityType {
    accountingConnectionId: uuid;
    provider: ProviderType;
    accessToken?: string | null;
    refreshToken?: string | null;
    tokenExpiry?: Date | null;
    providerMeta?: AccountingConnectionProviderMeta | null;
    connectionStatus: ConnectionStatusType;
    errorMessage?: string | null;
    lastBalanceFetchAt?: Date | null;

    /**
     * Create a new AccountingConnection, with an (optional) JSON object or AccountingConnection class
     *
     * @param {Object|AccountingConnection} doc - Can be either a simple JSON object or a AccountingConnection class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof AccountingConnection | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            accountingConnectionId: genUUID(),
            provider: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'accountingConnection';
        // @ts-ignore
        this.accountingConnectionId = doc._accountingConnectionId || doc.accountingConnectionId || doc.accountingconnectionid || genUUID();
        // @ts-ignore
        this.provider = util.trimRight(doc._provider) || util.trimRight(doc.provider) || null;
        // @ts-ignore
        this.accessToken = doc._accessToken || doc.accessToken || doc.accesstoken || null;
        // @ts-ignore
        this.refreshToken = doc._refreshToken || doc.refreshToken || doc.refreshtoken || null;
        // @ts-ignore
        this.tokenExpiry = DateUtil.timestampOnly(doc._tokenExpiry) || DateUtil.timestampOnly(doc.tokenExpiry) || DateUtil.timestampOnly(doc.tokenexpiry) || null;
        // @ts-ignore
        this.providerMeta = doc._providerMeta || doc.providerMeta || doc.providermeta || {};
        // @ts-ignore
        this.connectionStatus = util.trimRight(doc._connectionStatus) || util.trimRight(doc.connectionStatus) || util.trimRight(doc.connectionstatus) || 'disconnected';
        // @ts-ignore
        this.errorMessage = doc._errorMessage || doc.errorMessage || doc.errormessage || null;
        // @ts-ignore
        this.lastBalanceFetchAt = DateUtil.timestampOnly(doc._lastBalanceFetchAt) || DateUtil.timestampOnly(doc.lastBalanceFetchAt) || DateUtil.timestampOnly(doc.lastbalancefetchat) || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the accountingConnectionId across all entity types
     */
    get id(): uuid {
        return this.accountingConnectionId;
    }


    static PROVIDER_VALUES = ['xero', 'qbo', 'gusto'];
    
    static CONNECTION_STATUS_VALUES = ['connected', 'disconnected', 'error'];
    
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for this accounting connection"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.accountingConnectionId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return [];
    }

    /**
     * Default entries are Outlaw-supplied AccountingConnection entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): AccountingConnection[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'accountingConnection',
            table: 'ACCOUNTING_CONNECTION',
            comment: `Stores the OAuth credentials and connection state for a firm's external financial software integration (Xero, QuickBooks Online, or Gusto). Each firm may have at most one active connection. Holds encrypted access and refresh tokens, token expiry, provider-specific metadata (tenantId, realmId, companyUuid) used to map Outlaw bank accounts to external accounts, and a connection status that tracks whether the integration is active or in an error state.`,
            fields: {
                /**
                 * Unique ID for this accounting connection
                 */
                accountingConnectionId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for this accounting connection'
                },
                /**
                 * The accounting provider (xero or qbo)
                 */
                provider: {
                    type: 'char',
                    maxLength: 5,
                    values: ['xero', 'qbo', 'gusto'],
                    valueNames: ['Xero', 'QuickBooks Online', 'Gusto'],
                    required: true,
                    comment: 'The accounting provider: xero, qbo, or gusto'
                },
                /**
                 * OAuth access token (encrypted)
                 */
                accessToken: {
                    type: 'string',
                    encrypted: true,
                    comment: 'OAuth access token for API calls'
                },
                /**
                 * OAuth refresh token (encrypted)
                 */
                refreshToken: {
                    type: 'string',
                    encrypted: true,
                    comment: 'OAuth refresh token for obtaining new access tokens'
                },
                /**
                 * When the access token expires
                 */
                tokenExpiry: {
                    type: 'timestamp',
                    comment: 'When the access token expires'
                },
                /**
                 * Provider-specific metadata (e.g., realmId for QBO, tenantId for Xero)
                 */
                providerMeta: {
                    type: 'json',
                    jsdoc: '{realmId?: string, tenantId?: string, companyUuid?: string, useSandbox?: boolean}',
                    comment: 'Provider-specific metadata (realmId for QBO, tenantId for Xero, companyUuid for Gusto)'
                },
                /**
                 * Connection status
                 */
                connectionStatus: {
                    type: 'char',
                    maxLength: 12,
                    values: ['connected', 'disconnected', 'error'],
                    valueNames: ['Connected', 'Disconnected', 'Error'],
                    default: 'disconnected',
                    comment: 'Current connection status'
                },
                /**
                 * Error message if connection is in error state
                 */
                errorMessage: {
                    type: 'string',
                    comment: 'Error message if connection failed'
                },
                /**
                 * When the balance was last fetched
                 */
                lastBalanceFetchAt: {
                    type: 'timestamp',
                    comment: 'When the balance was last fetched from the provider'
                }
            }
        });
    }

}
export default AccountingConnection;
